/**
 * Rounds — the store half (docs/ROUNDS.md §4.2; DESIGN-CHANGELOG 2026-08-25
 * "Rounds: nominate with quarters, spin a wheel, on a page of its own" and
 * the entries above it). The prototype's shell owns this state in
 * `Games Tonight.dc.html` lines ~1975-2200; the rules live server-side in
 * migration 3039 and this slice only asks.
 *
 * NOT OPTIMISTIC, unlike the boards. Every round action is one RPC that
 * returns the round as it stands afterwards, and the answer is installed
 * whole: a nomination, a spin and a lock are each a few bytes, the refusals
 * (`leader only`, `no quarters left`, `wheel has been spun`) are the feature's
 * rules rather than network noise, and the one thing that must never happen
 * is a client showing a spin its server never recorded. `rBusy` is the guard
 * against a double press while the round trip is open.
 *
 * THE CLOCK. `clockOffsetMs` is `server − local`, sampled through
 * `serverNow()` a few times and kept at the lowest round trip; `serverNow()`
 * here is what every wheel pose is computed against. Resampled on every
 * SUBSCRIBED (the socket proving it is alive is a fine moment to re-check the
 * clock too), so a laptop that slept keeps landing with the crew.
 */

import type { StateCreator } from 'zustand';

import { R_ARM_MS } from '../../lib/round.ts';
import type { RoundSnapshot } from '../../lib/round.ts';
import { RepoError } from '../../repo/Repository.ts';
import type { QdStore } from '../types.ts';

/**
 * Which destructive control is armed — one at a time, by design.
 *
 * `kick:<user key>` is the one parameterised member. The alternative was a
 * second piece of state for "which seat", which would have needed its own
 * clearing on every screen change and every disarm; folding the seat into the
 * arm key means the existing timer, click-away and screen-change disarm all
 * cover it unchanged, and two rows can still never be armed at once.
 */
export type RoundArm = '' | 'leave' | 'unlock' | 'undo' | 'reset' | 'end' | `kick:${string}`;
export type RoundTierTab = 'all' | 's' | 'a' | 'b' | 'c';

/** localStorage key for the dismissed join card — per round id. */
export const R_SEEN_KEY = 'qd_round_seen_v1';

export interface RoundSlice {
  round: RoundSnapshot | null;
  /** A round RPC is open. */
  rBusy: boolean;
  rArm: RoundArm;
  /** The nominate rail's tier tab. */
  rTier: RoundTierTab;
  /** Desktop: the slice under the cursor and where the cursor is. */
  rHov: { id: number; x: number; y: number } | null;
  /** Phone: the NOMINATE bottom sheet. */
  rSheet: boolean;
  /** The round id whose one-shot join card was dismissed (`NOT TONIGHT`). */
  rSeen: string;
  /** `server − local`, ms. 0 until the first sample lands. */
  clockOffsetMs: number;
  clockSynced: boolean;

  /** Epoch ms on the SERVER's clock, as best we know it. */
  serverNow: () => number;
  /** Sample `server_now()` three times; keep the lowest-RTT estimate. */
  syncClock: () => Promise<void>;
  /** Refetch the round. Cheap, idempotent; the realtime answer to `round.changed`. */
  refreshRound: () => Promise<void>;

  rStart: () => Promise<void>;
  rOpen: () => void;
  rBack: () => void;
  rJoin: () => Promise<void>;
  rLeave: () => Promise<void>;
  /** Leader only: take a seat back. Arms first, commits on the second press. */
  rKick: (userKey: string) => Promise<void>;
  rDismiss: () => void;
  rNom: (appid: number) => Promise<void>;
  rClearMine: () => Promise<void>;
  rTglWt: () => Promise<void>;
  rSpin: () => Promise<void>;
  rLockIn: () => Promise<void>;
  rUnlock: () => Promise<void>;
  rUndoSpin: () => Promise<void>;
  rReset: () => Promise<void>;
  rEnd: () => Promise<void>;
  /** Arm a destructive control; auto-disarms after `R_ARM_MS`. `''` disarms. */
  rArmSet: (key: RoundArm) => void;
  rSetTier: (t: RoundTierTab) => void;
  rHovSet: (id: number | null, x?: number, y?: number) => void;
  rSheetOpen: () => void;
  rSheetClose: () => void;
}

/**
 * Where a round's page returns to: the SCHEDULE night it was started from
 * (3071, `schedNight`), or the board. One answer for BACK, END, the last one
 * out and a round that vanished under its page, so the four cannot disagree.
 */
function homeOf(r: RoundSnapshot | null): Partial<QdStore> {
  return r?.schedNight ? { screen: 'schedDay', schDay: r.schedNight } : { screen: 'tonight' };
}

function readSeen(): string {
  try {
    return typeof localStorage === 'undefined' ? '' : (localStorage.getItem(R_SEEN_KEY) ?? '');
  } catch {
    return '';
  }
}

function writeSeen(id: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(R_SEEN_KEY, id);
  } catch {
    /* private mode */
  }
}

export const createRoundSlice: StateCreator<QdStore, [], [], RoundSlice> = (set, get) => {
  let armTimer: ReturnType<typeof setTimeout> | null = null;
  let armAway: ((ev: Event) => void) | null = null;

  const disarmListener = (): void => {
    if (armAway && typeof document !== 'undefined') document.removeEventListener('click', armAway, true);
    armAway = null;
  };

  /**
   * One RPC, one answer, one toast on refusal. The server's reason is the
   * copy: `leader only` → `LEADER ONLY`. A network-class failure gets the
   * generic line instead of a fabricated rule.
   */
  const call = async (
    fn: () => Promise<RoundSnapshot | null>,
    after?: (r: RoundSnapshot | null) => void,
  ): Promise<void> => {
    if (get().rBusy) return;
    set({ rBusy: true });
    try {
      const r = await fn();
      set({ round: r });
      after?.(r);
    } catch (e) {
      const known = e instanceof RepoError && (e.code === 'FORBIDDEN' || e.code === 'NOT_FOUND');
      get().toast(known && e.message ? '✕ ' + e.message.toUpperCase() : "✕ COULDN'T REACH GAME NIGHT — TRY AGAIN", true);
      // A refusal usually means our copy of the round is stale (someone else
      // spun, locked, ended); the refetch is what puts the screen right.
      void get().refreshRound();
    } finally {
      set({ rBusy: false });
    }
  };

  /**
   * A crewmate who joined from the board and never opened SCHEDULE has no copy
   * of the night the round came from — fetch it on the way back, or the page
   * would say NOTHING NOMINATED YET over a night that has three.
   */
  const loadHome = (r: RoundSnapshot | null): void => {
    if (r?.schedNight) void get().schLoadNight(r.schedNight);
  };

  /** The second press commits; the first arms. */
  const armed = (key: RoundArm, commit: () => Promise<void>): Promise<void> => {
    if (get().rArm !== key) {
      get().rArmSet(key);
      return Promise.resolve();
    }
    get().rArmSet('');
    return commit();
  };

  return {
    round: null,
    rBusy: false,
    rArm: '',
    rTier: 'all',
    rHov: null,
    rSheet: false,
    rSeen: readSeen(),
    clockOffsetMs: 0,
    clockSynced: false,

    serverNow: () => Date.now() + get().clockOffsetMs,

    syncClock: async () => {
      const repo = get().repo;
      let best: { rtt: number; offset: number } | null = null;
      for (let i = 0; i < 3; i++) {
        const t0 = Date.now();
        let server: number;
        try {
          server = await repo.serverNow();
        } catch {
          continue;
        }
        const t1 = Date.now();
        const rtt = t1 - t0;
        const offset = server - (t0 + t1) / 2;
        if (!best || rtt < best.rtt) best = { rtt, offset };
      }
      if (best) set({ clockOffsetMs: Math.round(best.offset), clockSynced: true });
    },

    refreshRound: async () => {
      const s = get();
      if (!s.session) return;
      try {
        const r = await s.repo.roundGet();
        // A round that ended under us while its page was up: back to the
        // board, as `rEnd` itself does. The chip and the card follow `round`.
        if (!r && get().screen === 'round') {
          const was = get().round;
          set({ round: null, ...homeOf(was), rArm: '', rSheet: false });
          loadHome(was);
          return;
        }
        set({ round: r });
      } catch {
        /* transient — the next SUBSCRIBED or focus pass retries */
      }
    },

    rStart: () =>
      call(
        () => get().repo.roundStart(),
        () => {
          set({ screen: 'round', rArm: '', rTier: 'all', rHov: null, rSheet: false });
          if (typeof window !== 'undefined') window.scrollTo(0, 0);
        },
      ),

    rOpen: () => {
      set({ screen: 'round', rArm: '', rHov: null });
      if (typeof window !== 'undefined') window.scrollTo(0, 0);
    },

    rBack: () => {
      const r = get().round;
      set({ ...homeOf(r), rArm: '', rHov: null, rSheet: false });
      loadHome(r);
      if (typeof window !== 'undefined') window.scrollTo(0, 0);
    },

    rJoin: () =>
      call(
        () => get().repo.roundJoin(),
        () => {
          set({ screen: 'round', rArm: '', rTier: 'all', rHov: null, rSheet: false });
          if (typeof window !== 'undefined') window.scrollTo(0, 0);
        },
      ),

    rLeave: () =>
      armed('leave', async () => {
        const before = get().round;
        const me = get().session;
        await call(
          () => get().repo.roundLeave(),
          (r) => {
            if (!r) {
              set({ ...homeOf(before), rSheet: false });
              loadHome(before);
              get().toast('LEFT GAME NIGHT — LAST ONE OUT, SO IT CLOSED');
              return;
            }
            const moved = !!before && before.by === me && r.by !== me;
            if (moved) {
              const u = get().user(r.by);
              get().toast('LEFT GAME NIGHT — ' + (u ? u.name.toUpperCase() : 'SOMEONE') + ' PULLS THE LEVER NOW');
            } else {
              get().toast(before?.lock ? 'LEFT GAME NIGHT — THE PICK STANDS' : 'LEFT GAME NIGHT — QUARTERS REFUNDED');
            }
            set({ rSheet: false });
          },
        );
      }),

    /**
     * TAKE A SEAT BACK (Marcus, 2026-08-28). The refusals that matter —
     * leader only, not yourself, not somebody who already left — are the
     * server's, so this is the ordinary armed → one RPC → install shape; the
     * kicked player's own screen learns about it through `round.changed` like
     * every other seat move and falls to the "you're not in this" banner.
     */
    rKick: (userKey) =>
      armed(`kick:${userKey}`, async () => {
        const who = get().user(userKey);
        await call(
          () => get().repo.roundKick(userKey),
          () => get().toast((who ? who.name.toUpperCase() : 'THEY') + ' IS OUT OF GAME NIGHT'),
        );
      }),

    rDismiss: () => {
      const r = get().round;
      const id = r ? r.id : '';
      writeSeen(id);
      set({ rSeen: id });
    },

    rNom: (appid) => call(() => get().repo.roundNominate(appid)),
    rClearMine: () => call(() => get().repo.roundClearMine()),
    rTglWt: () => {
      const r = get().round;
      if (!r) return Promise.resolve();
      return call(() => get().repo.roundSetWeighted(!r.wtd));
    },
    rSpin: () => call(() => get().repo.roundSpin()),
    rLockIn: () =>
      call(
        () => get().repo.roundLock(),
        (r) => {
          const g = r?.spin ? get().gById(r.spin.winner) : null;
          get().toast('TONIGHT: ' + (g ? g.n.toUpperCase() : 'LOCKED'));
        },
      ),
    rUnlock: () =>
      armed('unlock', () =>
        call(
          () => get().repo.roundUnlock(),
          () => get().toast('UNLOCKED — THE PICK IS BACK IN PLAY'),
        ),
      ),
    rUndoSpin: () =>
      armed('undo', () =>
        call(
          () => get().repo.roundUndoSpin(),
          () => get().toast('SPIN UNDONE — NOMINATIONS ARE OPEN AGAIN'),
        ),
      ),
    rReset: () =>
      armed('reset', () =>
        call(
          () => get().repo.roundReset(),
          () => get().toast('GAME NIGHT RESET — EVERY QUARTER BACK'),
        ),
      ),
    rEnd: () =>
      armed('end', async () => {
        const r = get().round;
        const g = r?.lock && r.spin ? get().gById(r.spin.winner) : null;
        await call(
          async () => {
            await get().repo.roundEnd();
            return null;
          },
          () => {
            set({ ...homeOf(r), rSheet: false });
            loadHome(r);
            if (typeof window !== 'undefined') window.scrollTo(0, 0);
            get().toast(g ? 'GAME NIGHT ENDED · TONIGHT: ' + g.n.toUpperCase() : 'GAME NIGHT ENDED — NOTHING WAS LOCKED IN');
          },
        );
      }),

    /**
     * Armed states expire (changelog 2026-08-26): 5 s, or the first click that
     * lands outside any `[data-arm]` control. Arming a different control moves
     * the arm rather than stacking — one `SURE?` on screen, ever.
     */
    rArmSet: (key) => {
      if (armTimer) clearTimeout(armTimer);
      armTimer = null;
      disarmListener();
      set({ rArm: key });
      if (!key) return;
      armTimer = setTimeout(() => {
        armTimer = null;
        if (get().rArm === key) set({ rArm: '' });
      }, R_ARM_MS);
      if (typeof document !== 'undefined') {
        armAway = (ev) => {
          const t = ev.target as Element | null;
          if (t && typeof t.closest === 'function' && t.closest('[data-arm]')) return;
          if (get().rArm) set({ rArm: '' });
          disarmListener();
        };
        // Deferred one tick so the click that armed it is not the click that
        // disarms it.
        setTimeout(() => {
          if (armAway) document.addEventListener('click', armAway, true);
        }, 0);
      }
    },

    rSetTier: (rTier) => set({ rTier }),

    rHovSet: (id, x = 0, y = 0) => {
      const h = get().rHov;
      if (id === null) {
        if (h) set({ rHov: null });
        return;
      }
      // Throttled to 6px so a mousemove across the wheel is not one render
      // per pixel (prototype `rHovSet`).
      if (h && h.id === id && Math.abs(h.x - x) < 6 && Math.abs(h.y - y) < 6) return;
      set({ rHov: { id, x, y } });
    },

    rSheetOpen: () => set({ rSheet: true }),
    rSheetClose: () => set({ rSheet: false }),
  };
};
